import React from "react";
import { FileText } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { CreateStoreFormData } from "./createStoreSchema";
import { useTranslation } from "react-i18next";

interface LegalFiscalStepProps {
  form: UseFormReturn<CreateStoreFormData>;
}

export const LegalFiscalStep: React.FC<LegalFiscalStepProps> = ({ form }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-lg font-medium">
        <FileText className="h-5 w-5" />
        <h2>{t("legalFiscalStep.legalFiscalInformation")}</h2>
      </div>
      <Separator />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="taxId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("legalFiscalStep.vatId")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("legalFiscalStep.vatIdPlaceholder")}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="businessType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("legalFiscalStep.businessType")}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("legalFiscalStep.businessTypePlaceholder")}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="einzelunternehmen">
                    {t("legalFiscalStep.businessTypes.einzelunternehmen")}
                  </SelectItem>
                  <SelectItem value="gbr">
                    {t("legalFiscalStep.businessTypes.gbr")}
                  </SelectItem>
                  <SelectItem value="ohg">
                    {t("legalFiscalStep.businessTypes.ohg")}
                  </SelectItem>
                  <SelectItem value="kg">
                    {t("legalFiscalStep.businessTypes.kg")}
                  </SelectItem>
                  <SelectItem value="gmbh">
                    {t("legalFiscalStep.businessTypes.gmbh")}
                  </SelectItem>
                  <SelectItem value="ug">
                    {t("legalFiscalStep.businessTypes.ug")}
                  </SelectItem>
                  <SelectItem value="ag">
                    {t("legalFiscalStep.businessTypes.ag")}
                  </SelectItem>
                  <SelectItem value="other">
                    {t("legalFiscalStep.businessTypes.other")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="registrationNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("legalFiscalStep.registrationNumber")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t(
                    "legalFiscalStep.registrationNumberPlaceholder"
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="taxOffice"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("legalFiscalStep.taxOffice")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("legalFiscalStep.taxOfficePlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="hasVatExemption"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>{t("legalFiscalStep.vatExemption")}</FormLabel>
              <FormDescription>
                {t("legalFiscalStep.vatExemptionDescription")}
              </FormDescription>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="acceptTerms"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>{t("legalFiscalStep.termsAndConditions")}</FormLabel>
              <FormDescription>
                {t("legalFiscalStep.termsAndConditionsDescription")}
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
